
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function SupplierSection() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    country: '',
    website: '',
    fullName: '',
    email: '',
    howDidYouHear: '',
    additionalInfo: '',
    documentationReadiness: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate textarea length
    if (formData.additionalInfo.length > 500) {
      alert('Additional information must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3vjjumg28apfrolkjeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          country: '',
          website: '',
          fullName: '',
          email: '',
          howDidYouHear: '',
          additionalInfo: '',
          documentationReadiness: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const documentationRequirements = [
    {
      id: 'en71',
      title: 'EN 71 Test Reports',
      description: 'A valid EN 71 (Parts 1, 2, 3) test report issued by a Notified Body or an ILAC Accredited laboratory.',
      icon: 'ri-file-shield-line'
    },
    {
      id: 'risk',
      title: 'Risk Assessment',
      description: 'A comprehensive product Risk Analysis / Assessment, including its unique document number for traceability.',
      icon: 'ri-shield-check-line'
    },
    {
      id: 'bom',
      title: 'Bill of Materials (BOM)',
      description: 'A complete and detailed Bill of Materials for the product(s) in question.',
      icon: 'ri-list-check-line'
    },
    {
      id: 'quality',
      title: 'Quality Management Proof',
      description: 'Either the factory\'s valid ISO 9001 certificate or a detailed production workflow diagram.',
      icon: 'ri-award-line'
    },
    {
      id: 'specs',
      title: 'Product Specification Sheet',
      description: 'A technical data sheet detailing all product specifications.',
      icon: 'ri-file-text-line'
    },
    {
      id: 'imagery',
      title: 'Product Imagery',
      description: 'High-resolution, clear images of the toys from multiple angles.',
      icon: 'ri-image-line'
    },
    {
      id: 'technical',
      title: 'Technical Schematics (If Applicable)',
      description: 'For electric or battery-operated toys: detailed diagrams of sub-assemblies and electronic circuits.',
      icon: 'ri-blueprint-line'
    },
    {
      id: 'legal',
      title: 'Company Legal Documents',
      description: 'A copy of the Manufacturer\'s (Brand Owner\'s) valid Trade License.',
      icon: 'ri-building-line'
    },
    {
      id: 'brand',
      title: 'Brand Registration Certificate',
      description: 'The Brand Registration Certificate of the Manufacturer / Brand Owner.',
      icon: 'ri-copyright-line'
    },
    {
      id: 'authorization',
      title: 'Authorization Letter (If Applicable)',
      description: 'If you are an importer, trader, or distributor, a Letter of Authorization from the brand/product owner granting you rights to supply.',
      icon: 'ri-file-paper-line'
    },
    {
      id: 'lab',
      title: 'Lab Accreditation',
      description: 'The scope and accreditation certificate of the testing laboratory that issued the EN 71 report.',
      icon: 'ri-microscope-line'
    }
  ];

  return (
    <section 
      id="supplier" 
      className="py-20 relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/04f22dfbc74f65d89acbdd8036e80093.jpg')`
      }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Become a Supplier
          </h2>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Partner with Us to Deliver Safe and High-Quality Toys
          </p>
          <div className="max-w-5xl mx-auto text-gray-200 text-lg leading-relaxed">
            <p>
              We're always looking for passionate and reliable manufacturing partners who share our vision for creating safe, engaging, and high-quality toys for children. The first step in our journey together is a thorough qualification process to ensure we share the same commitment to excellence and product safety.
            </p>
            <p className="mt-4">
              To begin the evaluation, please be prepared to provide the following documentation. This helps us understand your operations and ensures a smooth and successful partnership from the start.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Required Documentation for Supplier Qualification
            </h3>
            <p className="text-lg text-gray-700 mb-8 text-center">
              Please submit the following for our initial review:
            </p>

            <div className="grid lg:grid-cols-2 gap-6">
              {documentationRequirements.map((req) => (
                <div key={req.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(req.id)}
                    className="w-full p-6 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                        <i className={`${req.icon} text-red-600 text-xl`}></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {req.title}
                        </h4>
                      </div>
                    </div>
                    <i className={`ri-arrow-${expandedSection === req.id ? 'up' : 'down'}-s-line text-gray-500 text-xl`}></i>
                  </button>
                  
                  {expandedSection === req.id && (
                    <div className="p-6 bg-white border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">
                        {req.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Supplier Contact Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Supplier Contact Form
            </h3>
            <p className="text-lg text-gray-700 mb-8 text-center">
              Please fill out the form below and our sourcing team will contact you shortly.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="supplier-form" className="space-y-8">
              {/* Company Information */}
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country/Region *
                    </label>
                    <div className="relative">
                      <select
                        id="country"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select country/region</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Botswana">Botswana</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Brunei">Brunei</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Canada">Canada</option>
                        <option value="Cape Verde">Cape Verde</option>
                        <option value="Central African Republic">Central African Republic</option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo">Congo</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominica">Dominica</option>
                        <option value="Dominican Republic">Dominican Republic</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Greece">Greece</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guinea-Bissau">Guinea-Bissau</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kiribati">Kiribati</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Laos">Laos</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libya">Libya</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Marshall Islands">Marshall Islands</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Micronesia">Micronesia</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar">Myanmar</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nauru">Nauru</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="North Korea">North Korea</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Palau">Palau</option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">Papua New Guinea</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                        <option value="Saint Lucia">Saint Lucia</option>
                        <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                        <option value="Samoa">Samoa</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Seychelles">Seychelles</option>
                        <option value="Sierra Leone">Sierra Leone</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Solomon Islands">Solomon Islands</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="South Sudan">South Sudan</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syria">Syria</option>
                        <option value="Taiwan">Taiwan</option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania">Tanzania</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Timor-Leste">Timor-Leste</option>
                        <option value="Togo">Togo</option>
                        <option value="Tonga">Tonga</option>
                        <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Tuvalu">Tuvalu</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Vanuatu">Vanuatu</option>
                        <option value="Vatican City">Vatican City</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="https://www.yourcompany.com"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Contact Person</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w---full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* How Did You Hear About Us */}
              <div>
                <label htmlFor="howDidYouHear" className="block text-sm font-medium text-gray-700 mb-2">
                  How Did You Hear About Us? *
                </label>
                <div className="relative">
                  <select
                    id="howDidYouHear"
                    name="howDidYouHear"
                    required
                    value={formData.howDidYouHear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-5  focus:border-transparent text-sm appearance-none bg-white"
                  >
                    <option value="">Please select</option>
                    <option value="Industry Referral">Industry Referral</option>
                    <option value="Trade Show/Exhibition">Trade Show/Exhibition</option>
                    <option value="Online Search">Online Search</option>
                    <option value="LinkedIn/Business Social Media">LinkedIn/Business Social Media</option>
                    <option value="Existing Business Partner">Existing Business Partner</option>
                    <option value="Previous Collaboration">Previous Collaboration</option>
                    <option value="Other">Other (please specify)</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={4}
                  maxLength={500}
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Please briefly describe your company's main products and manufacturing capabilities"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.additionalInfo.length}/500 characters
                </div>
              </div>

              {/* Documentation Readiness */}
              <div>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="documentationReadiness"
                    name="documentationReadiness"
                    checked={formData.documentationReadiness}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="documentationReadiness" className="ml-3 text-sm text-gray-700 cursor-pointer">
                    <strong>Documentation Readiness:</strong> We have read and understand the required documentation list (EN 71 reports, ISO certificates, etc.) and can provide these upon request.
                  </label>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  size="lg"
                  className="text-lg px-8"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Submit Supplier Application
                    </>
                  )}
                </Button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                  <i className="ri-check-circle-line mr-2"></i>
                  Thank you! Your supplier application has been submitted successfully. Our sourcing team will contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                  <i className="ri-error-warning-line mr-2"></i>
                  Sorry, there was an error submitting your application. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>

          <div className="text-center">
            <div className="grid md:grid-cols-3 gap-6 text-white">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-blue-600 text-2xl"></i>
                </div>
                <h5 className="text-lg font-semibold mb-2">Quality Assurance</h5>
                <p className="text-gray-200 text-sm">
                  Rigorous quality standards and safety compliance for all products
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-automq mb-4">
                  <i className="ri-handshake-line text-green-600 text-2xl"></i>
                </div>
                <h5 className="text-lg font-semibold mb-2">Long-term Partnership</h5>
                <p className="text-gray-200 text-sm">
                  Building lasting relationships with reliable manufacturing partners
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global- line text-purple-600 text-2xl"></i>
                </div>
                <h5 className="text-lg font-semibold mb-2">Global Reach</h5>
                <p className="text-gray-200 text-sm">
                  Access  </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
